import {
  type CountryCode,
  formatIncompletePhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'US';

// E.164: leading + and 1–15 digits, first digit non-zero.
const E164_REGEX = /^\+[1-9]\d{1,14}$/;

export function isE164(value: string): boolean {
  return E164_REGEX.test(value);
}

export function parsePhoneToE164(
  input: string,
  country: CountryCode
): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const direct = parsePhoneNumberFromString(trimmed, country);
  if (direct?.isValid()) return direct.number;

  // Recover doubled dial codes / mixed punctuation that libphonenumber
  // rejects as-written (e.g. "+1 +1 9175550100" from dial+E.164 paste).
  // Digit-strip keeps a single contiguous national/international digit
  // string that parses under the default country or as explicit +.
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return null;

  const asNational = parsePhoneNumberFromString(digits, country);
  if (asNational?.isValid()) return asNational.number;

  const asInternational = parsePhoneNumberFromString(`+${digits}`);
  if (asInternational?.isValid()) return asInternational.number;

  return null;
}

/**
 * Parses the send-to-signers inline phone editor: a country-code
 * selector plus a national input that may include its own "+".
 */
export function parseContactPhoneToE164(
  countryCode: string,
  nationalValue: string,
  country: CountryCode = DEFAULT_PHONE_COUNTRY
): string | null {
  const trimmed = nationalValue.trim();
  if (!trimmed) return null;
  const candidate = trimmed.startsWith('+')
    ? trimmed
    : `${countryCode} ${trimmed}`;
  return parsePhoneToE164(candidate, country);
}

const DEFAULT_EDITOR_CALLING_CODE = '+1';

/**
 * Split a stored phone into the inline editor's country-code selector
 * plus national input. Compact E.164 (no space after the calling code)
 * is parsed with libphonenumber; spaced display values keep the legacy
 * split so unsaved / pre-normalization numbers still reopen.
 */
export function splitContactPhoneForEditor(phone: string): {
  countryCode: string;
  national: string;
} {
  const parsed = parsePhoneNumberFromString(phone);
  if (parsed?.countryCallingCode && parsed.nationalNumber) {
    return {
      countryCode: `+${parsed.countryCallingCode}`,
      national: parsed.formatNational() || parsed.nationalNumber,
    };
  }

  const phoneMatch = phone.match(/^(\+\d+)\s+(.+)$/);
  if (phoneMatch) {
    return { countryCode: phoneMatch[1], national: phoneMatch[2] };
  }

  return { countryCode: DEFAULT_EDITOR_CALLING_CODE, national: phone };
}

export function explodeE164(
  e164: string
): { country: CountryCode; national: string } | null {
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed || !parsed.country) return null;
  return {
    country: parsed.country,
    national: parsed.formatNational(),
  };
}

// International format with the country calling code (e.g. "+1 555 555
// 0100"). Falls back to the raw value when it can't be parsed.
export function formatE164Display(e164: string): string {
  const parsed = parsePhoneNumberFromString(e164);
  return parsed ? parsed.formatInternational() : e164;
}

export function formatPartial(input: string, country: CountryCode): string {
  return formatIncompletePhoneNumber(input, country);
}

// Country calling code + national number using the region's national
// punctuation, e.g. "+1 9177960611" -> "+1 (917) 796-0611". Falls back to
// the raw value when it can't be parsed (partial entry, unknown region).
export function formatPhoneNational(value: string): string {
  const parsed = parsePhoneNumberFromString(value.replace(/[^\d+]/g, ''));
  if (!parsed) return value;
  return `+${parsed.countryCallingCode} ${parsed.formatNational()}`;
}

/**
 * Formats a phone input as the user types ("4155552671" ->
 * "(415) 555-2671"). `previous` is the field's value before this edit:
 * backspacing over punctuation the formatter added would otherwise put it
 * straight back, so that deletes the digit before it instead.
 */
export function formatPhoneAsYouType(
  next: string,
  previous: string,
  country: CountryCode = DEFAULT_PHONE_COUNTRY
): string {
  let value = next;
  const digitsOf = (text: string) => text.replace(/\D/g, '');
  if (
    next.length < previous.length &&
    digitsOf(next) === digitsOf(previous) &&
    digitsOf(next)
  ) {
    value = next.replace(/\d(?=\D*$)/, '');
  }
  const digits = digitsOf(value);
  if (!digits) return value.startsWith('+') ? '+' : '';
  // With a "+" the country code is explicit, so libphonenumber knows how to
  // group it. Without one it's a local number: libphonenumber would read a
  // leading 1 as the US country code ("123…" -> "1 (23…"), so mask it here.
  if (value.trim().startsWith('+') || country !== 'US') {
    return formatIncompletePhoneNumber(value, country);
  }
  if (digits.length > 11) return digits;
  const hasTrunk = digits.length === 11 && digits.startsWith('1');
  const local = hasTrunk ? digits.slice(1) : digits.slice(0, 10);
  const area = local.slice(0, 3);
  const exchange = local.slice(3, 6);
  const line = local.slice(6);
  let masked = local.length > 3 ? `(${area}) ${exchange}` : area;
  if (line) masked += `-${line}`;
  return hasTrunk ? `1 ${masked}` : masked;
}
